import { useState, useEffect, useCallback, useMemo } from 'react';
import { findAnnotationAtTime, getAnnotationColor, countAnnotationsByType, getNavigationAnnotation, getVolleyStrokeIds, parseJSONAsync, applyInOutCorrection, findEntryAtOrBefore } from '../utils/annotationUtils';
import { snapToFrame, getFrameTolerance, normalizeTimeToFrame } from '../utils/timeUtils';
import { getFPS, setFPS, ANNOTATION_TYPES, MANUAL_LINE_CONFIDENCE, NO_BOUNCE_FRAME } from '../utils/constants';
import { getFrameForTime, getTimeForFrame, isFrameIndexReady } from '../utils/frameIndex';
import { usageTrackingService } from '../services/usageTracking';

export const useAnnotations = (currentTime, playerRef, onFirstAnnotation = null, onUpgradeNeeded = null) => {
  const [annotations, setAnnotations] = useState([]);
  const [playerAnnotations, setPlayerAnnotations] = useState([]);
  const [trajectories, setTrajectories] = useState([]);
  const [annotationMode, setAnnotationMode] = useState('stroke');

  // Raw records from an uploaded strokes-array file, kept verbatim so edits can be
  // written back out without losing the fields the app doesn't model.
  const [strokeRecords, setStrokeRecords] = useState(null);

  // Custom labels for review workflow (e.g., ["FP"] for false positives)
  const [customLabels, setCustomLabels] = useState([]);

  // Frame-indexed Maps for O(1) lookups instead of O(n) .filter() on every frame
  // Uses frame index for exact PTS→frame mapping (falls back to FPS-based math)
  const annotationsByFrame = useMemo(() => {
    if (annotations.length === 0) return new Map();
    const map = new Map();
    for (const ann of annotations) {
      const frame = getFrameForTime(ann.timestamp);
      if (!map.has(frame)) map.set(frame, []);
      map.get(frame).push(ann);
    }
    return map;
  }, [annotations]);

  const playerAnnotationsByFrame = useMemo(() => {
    if (playerAnnotations.length === 0) return new Map();
    const map = new Map();
    for (const ann of playerAnnotations) {
      const frame = getFrameForTime(ann.timestamp);
      if (!map.has(frame)) map.set(frame, []);
      map.get(frame).push(ann);
    }
    return map;
  }, [playerAnnotations]);

  // Stroke details state
  const [currentPlayer, setCurrentPlayer] = useState('bottom');
  const [currentStrokeType, setCurrentStrokeType] = useState('forehand');

  // Player filter state
  const [playerFilter, setPlayerFilter] = useState('all');

  // Stroke type filter state
  const [strokeTypeFilter, setStrokeTypeFilter] = useState('all');

  // Volley filter: when true, stroke navigation only visits strokes with no bounce before the next stroke
  const [volleyFilter, setVolleyFilter] = useState(false);

  // Calculate current annotations synchronously with useMemo (eliminates async state lag)
  // Uses frame-indexed Map for O(1) lookup instead of O(n) filter
  const currentAnnotations = useMemo(() => {
    if (!playerRef.current) return [];

    const videoCurrentTime = playerRef.current.currentTime();
    // Use frame index for exact PTS→frame mapping
    const frameNumber = getFrameForTime(videoCurrentTime);

    return annotationsByFrame.get(frameNumber) || [];
  }, [currentTime, annotationsByFrame]);

  // Update annotation type when mode changes
  useEffect(() => {
    if (!playerRef.current) return;

    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);

    setAnnotations(prev => prev.map(ann =>
      getFrameForTime(ann.timestamp) === currentFrame
        ? { ...ann, type: annotationMode }
        : ann
    ));
  }, [annotationMode]);

  // Set player directly (replaces cyclePlayer)
  const setPlayer = (player) => {
    if (player !== 'top' && player !== 'bottom') return;

    setCurrentPlayer(player);

    // Update any existing stroke annotation at current time
    if (!playerRef.current) return;

    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);

    setAnnotations(prevAnnotations => prevAnnotations.map(ann =>
      getFrameForTime(ann.timestamp) === currentFrame && ann.type === 'stroke'
        ? { ...ann, player }
        : ann
    ));
  };

  // Set stroke type directly (replaces cycleShotType)
  const setStrokeType = (strokeType) => {
    const validTypes = ['forehand', 'backhand', 'serve', 'smash'];
    if (!validTypes.includes(strokeType)) return;

    setCurrentStrokeType(strokeType);

    // Update any existing stroke annotation at current time
    if (!playerRef.current) return;

    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);

    setAnnotations(prevAnnotations => prevAnnotations.map(ann =>
      getFrameForTime(ann.timestamp) === currentFrame && ann.type === 'stroke'
        ? { ...ann, strokeType }
        : ann
    ));
  };

  // Cycle through player positions (kept for backward compatibility)
  const cyclePlayer = () => {
    setCurrentPlayer(prev => {
      const newPlayer = prev === 'bottom' ? 'top' : 'bottom';

      // Update any existing stroke annotation at current time
      if (!playerRef.current) return newPlayer;

      const videoCurrentTime = playerRef.current.currentTime();
      const currentFrame = getFrameForTime(videoCurrentTime);

      setAnnotations(prevAnnotations => prevAnnotations.map(ann =>
        getFrameForTime(ann.timestamp) === currentFrame && ann.type === 'stroke'
          ? { ...ann, player: newPlayer }
          : ann
      ));

      return newPlayer;
    });
  };

  // Cycle through shot types (kept for backward compatibility)
  const cycleShotType = () => {
    const shotTypes = ['forehand', 'backhand', 'serve', 'smash'];
    setCurrentStrokeType(prev => {
      const currentIndex = shotTypes.indexOf(prev);
      const nextIndex = (currentIndex + 1) % shotTypes.length;
      const newShotType = shotTypes[nextIndex];

      // Update any existing stroke annotation at current time
      if (!playerRef.current) return newShotType;

      const videoCurrentTime = playerRef.current.currentTime();
      const currentFrame = getFrameForTime(videoCurrentTime);

      setAnnotations(prevAnnotations => prevAnnotations.map(ann =>
        getFrameForTime(ann.timestamp) === currentFrame && ann.type === 'stroke'
          ? { ...ann, strokeType: newShotType }
          : ann
      ));

      return newShotType;
    });
  };

  // Add annotation at current time
  // Returns true if annotation was added, false if blocked (e.g., usage limit)
  const addAnnotation = async (x, y, type = annotationMode, overridePlayer = null, overrideStrokeType = null) => {
    if (!playerRef.current) return false;

    const videoCurrentTime = playerRef.current.currentTime();
    // Use frame index for exact frame↔time mapping
    const currentFrame = getFrameForTime(videoCurrentTime);
    const snappedTime = getTimeForFrame(currentFrame);

    const existingAnnotation = annotations.find(ann =>
      getFrameForTime(ann.timestamp) === currentFrame
    );

    // Use override values if provided, otherwise use current state
    const finalPlayer = overridePlayer !== null ? overridePlayer : currentPlayer;
    const finalStrokeType = overrideStrokeType !== null ? overrideStrokeType : currentStrokeType;

    if (existingAnnotation) {
      // Updating existing annotation - doesn't count as new usage
      // Preserve flaggedForReview and customLabel from the existing annotation
      setAnnotations(prev => prev.map(ann =>
        getFrameForTime(ann.timestamp) === currentFrame
          ? { ...ann, x, y, type, player: finalPlayer, strokeType: finalStrokeType,
              flaggedForReview: ann.flaggedForReview, customLabel: ann.customLabel }
          : ann
      ));
      return true;
    } else {
      // Creating NEW annotation - check usage FIRST before adding
      try {
        const result = await usageTrackingService.trackAnnotationCreated(type, finalStrokeType);
        
        if (!result.success && result.needsUpgrade) {
          console.warn('Usage limit reached - blocking annotation');
          // Trigger upgrade modal callback if provided
          if (onUpgradeNeeded) {
            onUpgradeNeeded('limit_reached');
          }
          return false;
        }
        
        if (!result.canContinue) {
          console.warn('Cannot add annotation:', result.error);
          return false;
        }
      } catch (err) {
        console.error('Error checking usage:', err);
        // On error, allow the annotation but log it (fail open for better UX)
      }

      // Usage check passed - add the annotation
      const newAnnotation = {
        id: Date.now(),
        x,
        y,
        type,
        timestamp: snappedTime,
        player: finalPlayer,
        strokeType: finalStrokeType
      };

      setAnnotations(prev => {
        const newAnnotations = [...prev, newAnnotation];

        // Trigger callback on first annotation if provided
        if (onFirstAnnotation && prev.length === 0) {
          onFirstAnnotation();
        }

        return newAnnotations;
      });
      
      return true;
    }
  };

  // Remove annotation at current time
  const removeAnnotation = () => {
    if (!playerRef.current) return;

    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);

    setAnnotations(prev => prev.filter(ann =>
      getFrameForTime(ann.timestamp) !== currentFrame
    ));
  };

  // Switch annotation mode
  const switchAnnotationMode = () => {
    const currentIndex = ANNOTATION_TYPES.indexOf(annotationMode);
    const nextIndex = (currentIndex + 1) % ANNOTATION_TYPES.length;
    setAnnotationMode(ANNOTATION_TYPES[nextIndex]);
  };

  // Annotation counts — only recalculated when annotations change
  const annotationCounts = useMemo(() => countAnnotationsByType(annotations), [annotations]);

  // Set of stroke IDs that qualify as volleys. A strokes file records this directly via
  // the bounce_frame sentinel, so trust it; regular annotation files carry no such field,
  // so fall back to inferring volleys from the surrounding bounce/net annotations.
  const volleyStrokeIds = useMemo(() => {
    const statesVolleysDirectly = annotations.some(ann => ann.isVolley !== undefined);
    if (!statesVolleysDirectly) return getVolleyStrokeIds(annotations);

    const ids = new Set();
    for (const ann of annotations) {
      if (ann.isVolley) ids.add(ann.id);
    }
    return ids;
  }, [annotations]);

  // Review counts: total flagged, and how many have been labeled (customLabel !== undefined and !== 'none')
  const reviewCounts = useMemo(() => {
    const flagged = annotations.filter(a => a.flaggedForReview);
    const labeled = flagged.filter(a => a.customLabel && a.customLabel !== 'none');
    return { total: flagged.length, labeled: labeled.length, remaining: flagged.length - labeled.length };
  }, [annotations]);

  // Pre-sort timestamps per type once when annotations or filters change (O(n)).
  // Then binary-search for the count at currentTime each frame (O(log n)).
  const _sortedByType = useMemo(() => {
    const result = { stroke: [], bounce: [], inFlight: [], net: [] };
    for (const ann of annotations) {
      if (playerFilter !== 'all' && ann.player !== playerFilter) continue;
      if (strokeTypeFilter !== 'all' && ann.type === 'stroke' && ann.strokeType !== strokeTypeFilter) continue;
      if (volleyFilter && ann.type === 'stroke' && !volleyStrokeIds.has(ann.id)) continue;
      const key = ann.type === 'in-flight' ? 'inFlight' : ann.type;
      if (result[key]) result[key].push(ann.timestamp);
    }
    for (const key of Object.keys(result)) result[key].sort((a, b) => a - b);
    return result;
  }, [annotations, playerFilter, strokeTypeFilter, volleyFilter, volleyStrokeIds]);

  // Pre-sort flagged-for-review timestamps for progress tracking
  const _sortedReviewTimestamps = useMemo(() => {
    return annotations
      .filter(a => a.flaggedForReview)
      .map(a => a.timestamp)
      .sort((a, b) => a - b);
  }, [annotations]);

  // Sorted volley timestamps (respects player + strokeType filters for accurate bounds/count)
  const volleyProgress = useMemo(() => {
    const ts = annotations
      .filter(a =>
        a.type === 'stroke' &&
        volleyStrokeIds.has(a.id) &&
        (playerFilter === 'all' || a.player === playerFilter) &&
        (strokeTypeFilter === 'all' || a.strokeType === strokeTypeFilter)
      )
      .map(a => a.timestamp)
      .sort((a, b) => a - b);
    const total = ts.length;
    let lo = 0, hi = total;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (ts[mid] <= currentTime) lo = mid + 1; else hi = mid;
    }
    return { current: lo, total, sortedTimestamps: ts };
  }, [annotations, volleyStrokeIds, currentTime, playerFilter, strokeTypeFilter]);

  const annotationProgress = useMemo(() => {
    const progress = {};
    for (const key of ['stroke', 'bounce', 'inFlight', 'net']) {
      const ts = _sortedByType[key];
      const total = ts.length;
      let lo = 0, hi = ts.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (ts[mid] <= currentTime) lo = mid + 1; else hi = mid;
      }
      progress[key] = { current: lo, total };
    }
    // Review progress
    const rts = _sortedReviewTimestamps;
    let rlo = 0, rhi = rts.length;
    while (rlo < rhi) {
      const mid = (rlo + rhi) >> 1;
      if (rts[mid] <= currentTime) rlo = mid + 1; else rhi = mid;
    }
    progress.review = { current: rlo, total: rts.length };
    return progress;
  }, [_sortedByType, _sortedReviewTimestamps, currentTime]);

  // Navigation functions
  const navigateToAnnotation = (type, direction = 'next', playerFilterParam = playerFilter, strokeTypeFilterParam = strokeTypeFilter) => {
    if (!playerRef.current) return;

    const currentFPS = getFPS();
    if (!currentFPS) {
      console.error('Cannot navigate to annotation: FPS not set');
      return;
    }

    const activeVolleyIds = (volleyFilter && type === 'stroke') ? volleyStrokeIds : null;
    const annotation = getNavigationAnnotation(annotations, currentTime, type, direction, playerFilterParam, strokeTypeFilterParam, activeVolleyIds);
    if (annotation && playerRef.current) {
      playerRef.current.currentTime(annotation.timestamp);
    }
  };

  // Navigate to next/previous flagged-for-review annotation
  const navigateToReview = (direction = 'next') => {
    if (!playerRef.current) return;
    const flagged = annotations
      .filter(a => a.flaggedForReview)
      .sort((a, b) => a.timestamp - b.timestamp);
    if (flagged.length === 0) return;

    let target;
    if (direction === 'next') {
      target = flagged.find(a => a.timestamp > currentTime);
    } else {
      target = [...flagged].reverse().find(a => a.timestamp < currentTime);
    }
    if (target) {
      playerRef.current.currentTime(target.timestamp);
    }
  };

  // Group annotations by rallyId, computing start/end time for each rally.
  // Sorted ascending by rallyId so prev/next navigation is well-defined.
  const rallyGroups = useMemo(() => {
    const map = new Map();
    for (const a of annotations) {
      if (a.rallyId == null) continue;
      if (!map.has(a.rallyId)) {
        map.set(a.rallyId, { rallyId: a.rallyId, startTime: Infinity, endTime: -Infinity });
      }
      const g = map.get(a.rallyId);
      if (a.timestamp < g.startTime) g.startTime = a.timestamp;
      if (a.timestamp > g.endTime)   g.endTime   = a.timestamp;
    }
    return [...map.values()].sort((a, b) => a.rallyId - b.rallyId);
  }, [annotations]);

  // Navigate to the previous or next rally (by startTime) and seek to its
  // endTime or startTime depending on `jumpTarget`.
  const navigateToRally = (direction = 'next', jumpTarget = 'end') => {
    if (!playerRef.current || rallyGroups.length === 0) return;
    let target;
    if (direction === 'next') {
      target = rallyGroups.find(r => r.startTime > currentTime);
    } else {
      // Compare against the same time we'd be sitting at after a seek so that
      // clicking previous from a rally's endTime correctly lands on the
      // preceding rally rather than looping back to the same one.
      if (jumpTarget === 'end') {
        target = [...rallyGroups].reverse().find(r => r.endTime < currentTime);
      } else {
        target = [...rallyGroups].reverse().find(r => r.startTime < currentTime);
      }
    }
    if (target) {
      const seekTime = jumpTarget === 'start' ? target.startTime : target.endTime;
      playerRef.current.currentTime(seekTime);
    }
  };

  // Navigate to next/previous annotation that has this custom label
  const navigateToCustomLabel = (label, direction = 'next') => {
    if (!playerRef.current || !label) return;
    const matches = annotations
      .filter(a => a.customLabel === label)
      .sort((a, b) => a.timestamp - b.timestamp);
    if (matches.length === 0) return;

    let target;
    if (direction === 'next') {
      target = matches.find(a => a.timestamp > currentTime);
    } else {
      target = [...matches].reverse().find(a => a.timestamp < currentTime);
    }
    if (target) {
      playerRef.current.currentTime(target.timestamp);
    }
  };

  // Cycle the custom label on the annotation at the current frame
  const cycleCustomLabel = () => {
    if (!playerRef.current) return;
    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);

    const cycle = ['none', ...customLabels];
    setAnnotations(prev => prev.map(ann => {
      if (getFrameForTime(ann.timestamp) !== currentFrame) return ann;
      const currentLabel = ann.customLabel || 'none';
      const idx = cycle.indexOf(currentLabel);
      const nextLabel = cycle[(idx + 1) % cycle.length];
      return { ...ann, customLabel: nextLabel === 'none' ? undefined : nextLabel };
    }));
  };

  // Toggle flaggedForReview on the annotation at the current frame
  const toggleFlagForReview = () => {
    if (!playerRef.current) return;
    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);

    setAnnotations(prev => prev.map(ann => {
      if (getFrameForTime(ann.timestamp) !== currentFrame) return ann;
      return { ...ann, flaggedForReview: !ann.flaggedForReview };
    }));
  };

  // Frame-sorted timeline of in/out calls, rebuilt only when annotations change rather
  // than on every frame. Frames rather than raw timestamps keep the comparison integral.
  const strokeInTimeline = useMemo(() => (
    annotations
      .filter(ann => typeof ann.strokeIn === 'boolean')
      .map(ann => ({
        frame: getFrameForTime(ann.timestamp),
        in: ann.strokeIn,
        lineConfidence: ann.lineConfidence ?? null,
        strokeIndex: ann.strokeIndex ?? null,
      }))
      .sort((a, b) => a.frame - b.frame)
  ), [annotations]);

  // The call of the most recent stroke at or before the playhead, so the badge stays up
  // until the next stroke. Null before the first stroke, and for files with no `in` field.
  const currentStrokeInfo = useMemo(() => {
    if (!playerRef.current || strokeInTimeline.length === 0) return null;
    const frame = getFrameForTime(playerRef.current.currentTime());
    return findEntryAtOrBefore(strokeInTimeline, frame);
  }, [currentTime, strokeInTimeline]);

  // Keep a scalar for the badge condition check (preserves old API)
  const currentStrokeIn = currentStrokeInfo ? currentStrokeInfo.in : null;

  // Set the call of whichever stroke the badge is showing — the most recent one at or
  // before the playhead, so this works between strokes too. The raw record is updated as
  // well, so the next strokes-file save carries the correction. Because the value is
  // human-set, line_confidence is pinned to MANUAL_LINE_CONFIDENCE and the model's
  // original call is preserved once, on the first edit. Setting the call the model already
  // made is still meaningful: it records that a human confirmed it.
  const setStrokeInCall = (nextIn) => {
    if (!playerRef.current || !strokeRecords) return;

    const frame = getFrameForTime(playerRef.current.currentTime());
    const target = findEntryAtOrBefore(strokeInTimeline, frame);
    if (!target || target.strokeIndex == null) return;

    const { strokeIndex } = target;

    setAnnotations(prev => prev.map(ann => (
      ann.strokeIndex === strokeIndex
        ? { ...ann, strokeIn: nextIn, lineConfidence: MANUAL_LINE_CONFIDENCE }
        : ann
    )));

    setStrokeRecords(prev => prev.map((record, i) => (
      i === strokeIndex ? applyInOutCorrection(record, nextIn) : record
    )));
  };

  // Write the uploaded strokes file back out in its original shape: an array of records
  // with every field preserved verbatim, carrying the in/out corrections plus the review
  // flag and custom label held on the matching annotation. Records whose annotation was
  // deleted are dropped; strokes added in the app have no raw record and can't be
  // represented in this format, so they only appear in the all-annotations export.
  const buildStrokeRecordsForSave = () => {
    if (!strokeRecords) return null;

    const annotationByIndex = new Map();
    for (const ann of annotations) {
      if (ann.strokeIndex != null) annotationByIndex.set(ann.strokeIndex, ann);
    }

    return strokeRecords.reduce((acc, record, i) => {
      const ann = annotationByIndex.get(i);
      if (!ann) return acc;

      const merged = { ...record };

      // Write the flag back to whichever key the record arrived with, so a
      // round-trip can't leave a snake_case and a camelCase copy disagreeing.
      const presentFlagKeys = ['flagged_for_review', 'flaggedForReview'].filter(key => key in record);
      for (const key of (presentFlagKeys.length ? presentFlagKeys : ['flaggedForReview'])) {
        merged[key] = !!ann.flaggedForReview;
      }

      if (ann.customLabel && ann.customLabel !== 'none') {
        merged.customLabel = ann.customLabel;
      } else {
        delete merged.customLabel;
      }

      acc.push(merged);
      return acc;
    }, []);
  };

  // Tags the export with who made the calls, e.g. strokes_abc123_jane_doe.json
  const saveStrokesFile = (sourceFileName = '', userName = '') => {
    const records = buildStrokeRecordsForSave();
    if (!records) return false;

    const jsonString = JSON.stringify(records, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const baseName = sourceFileName.replace(/\.[^/.]+$/, '') || 'strokes';
    const suffix = userName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const a = document.createElement('a');
    a.href = url;
    a.download = suffix ? `${baseName}_${suffix}.json` : `${baseName}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  };

  // Add a new custom label to the cycle
  const addCustomLabel = (label) => {
    if (!label || label === 'none') return;
    const trimmed = label.trim();
    if (!trimmed) return;
    setCustomLabels(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
  };

  // Remove a custom label from the cycle (also clears it from any annotations using it)
  const removeCustomLabel = (label) => {
    setCustomLabels(prev => prev.filter(l => l !== label));
    setAnnotations(prev => prev.map(ann =>
      ann.customLabel === label ? { ...ann, customLabel: undefined } : ann
    ));
  };

  // Save annotations to JSON
  const saveAnnotations = (videoUrl, videoDuration) => {
    if (!playerRef.current) return;

    const currentFPS = getFPS();
    if (!currentFPS) {
      console.error('Cannot save annotations: FPS not set');
      return;
    }

    const actualVideoWidth = playerRef.current?.videoWidth || 1280;
    const actualVideoHeight = playerRef.current?.videoHeight || 720;

    let videoFilename = '';
    try {
      if (videoUrl) {
        const urlWithoutParams = videoUrl.split('?')[0];
        const urlParts = urlWithoutParams.split('/');
        const lastPart = urlParts[urlParts.length - 1];
        if (lastPart.toLowerCase().endsWith('.mp4')) {
          videoFilename = lastPart;
        }
      }
    } catch (error) {
      console.warn('Could not extract video filename from URL:', error);
    }

    const annotationsData = annotations.map(annotation => {
      // Convert exact PTS back to frame/fps format for file compatibility
      const frame = getFrameForTime(annotation.timestamp);
      const fileTime = currentFPS ? frame / currentFPS : annotation.timestamp;
      const annotationData = {
        time: fileTime,
        timestamp: formatTime(fileTime),
        x: Math.round(annotation.x),
        y: Math.round(annotation.y),
        type: annotation.type,
        player: annotation.player || 'bottom',
        strokeType: annotation.strokeType || 'forehand'
      };

      // Add meter coordinates if they exist (from camera calibration)
      if (annotation.x_m !== undefined && annotation.y_m !== undefined) {
        annotationData.x_m = annotation.x_m;
        annotationData.y_m = annotation.y_m;
        if (annotation.z_m !== undefined) {
          annotationData.z_m = annotation.z_m;
        }
      }

      // Add bounding box data if it exists
      if (annotation.boundingBox) {
        annotationData.boundingBox = {
          x1: Math.round(annotation.boundingBox.x1),
          y1: Math.round(annotation.boundingBox.y1),
          x2: Math.round(annotation.boundingBox.x2),
          y2: Math.round(annotation.boundingBox.y2)
        };
      }

      if (annotation.flaggedForReview) {
        annotationData.flaggedForReview = true;
      }
      if (annotation.customLabel && annotation.customLabel !== 'none') {
        annotationData.customLabel = annotation.customLabel;
      }

      // Persist scoring, player ID and rally fields for stroke annotations
      if (annotation.type === 'stroke') {
        if (annotation.playerId)                                  annotationData.playerId    = annotation.playerId;
        if (annotation.pointScore)                                annotationData.pointScore  = annotation.pointScore;
        if (annotation.gameScore)                                 annotationData.gameScore   = annotation.gameScore;
        if (annotation.setScore)                                  annotationData.setScore    = annotation.setScore;
        if (annotation.rallyId != null)                           annotationData.rallyId     = annotation.rallyId;
      }

      return annotationData;
    });

    // Save player annotations with stroke signal data
    const playerAnnotationsData = playerAnnotations.map(playerAnnotation => {
      // Convert exact PTS back to frame/fps format for file compatibility
      const frame = getFrameForTime(playerAnnotation.timestamp);
      const fileTime = currentFPS ? frame / currentFPS : playerAnnotation.timestamp;
      return {
        time: fileTime,
        timestamp: formatTime(fileTime),
      player: playerAnnotation.player,
      boundingBox: {
        x1: Math.round(playerAnnotation.boundingBox.x1),
        y1: Math.round(playerAnnotation.boundingBox.y1),
        x2: Math.round(playerAnnotation.boundingBox.x2),
        y2: Math.round(playerAnnotation.boundingBox.y2)
      },
      strokeScore: playerAnnotation.strokeScore || 0,
      strokeType: playerAnnotation.strokeType || null
      };
    });

    const data = {
      video_filename: videoFilename,
      video_width: actualVideoWidth,
      video_height: actualVideoHeight,
      fps: currentFPS,
      total_frames: Math.floor(videoDuration * currentFPS),
      total_duration: formatTime(videoDuration),
      ball_annotations: annotationsData,
      player_annotations: playerAnnotationsData,
      court_keypoints: [], // Keep for compatibility
      ...(trajectories.length > 0 && { trajectories }),
    };

    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const a = document.createElement('a');
    a.href = url;
    a.download = videoFilename ? `${videoFilename}_annotations_${timestamp}.json` : `annotations_${timestamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Load annotations from JSON - accepts either a File/Blob or pre-parsed data object
  // Using pre-parsed data avoids redundant JSON.parse calls for large files
  const loadAnnotations = async (fileOrData) => {
    try {
      let rawData;
      let fileName = '';

      if (fileOrData instanceof File || fileOrData instanceof Blob) {
        fileName = fileOrData.name || '';
        const text = await fileOrData.text();
        // Use Web Worker for large files (>5MB) to avoid blocking UI
        rawData = await parseJSONAsync(text);
      } else {
        // Pre-parsed data passed directly (avoids double-parsing)
        rawData = fileOrData;
      }

      // Check if this is the new stroke format (array of stroke objects)
      const isNewStrokeFormat = Array.isArray(rawData) &&
        rawData.length > 0 &&
        rawData[0].player_x1_px !== undefined &&
        rawData[0].opponent_x1_px !== undefined;

      let data, annotationsArray, loadedAnnotations, loadedPlayerAnnotations;
      let loadedStrokeRecords = null;

      if (isNewStrokeFormat) {
        // New format: array of stroke objects
        // Convert frame numbers to timestamps using frame index (exact PTS) or FPS fallback.
        // Annotation files store time as frame_number / annotation_fps.
        // We recover the frame number and look up the exact PTS from the MP4 sample table.
        const fps = getFPS();
        if (!fps) {
          throw new Error('No FPS value found in annotation file and no FPS set in app');
        }

        // Process each stroke entry
        loadedAnnotations = [];
        loadedPlayerAnnotations = [];
        loadedStrokeRecords = rawData.map(stroke => ({ ...stroke }));

        rawData.forEach((stroke, i) => {
          // Recover frame number from time: frame = round(time * fps)
          const frameNumber = Math.round((stroke.time || 0) * fps);
          // Look up exact PTS from frame index (falls back to frame/fps if index unavailable)
          const timestamp = getTimeForFrame(frameNumber);

          // Determine which player is hitting based on player_y_m
          // Negative y_m = bottom player, positive y_m = top player
          const hittingPlayer = (stroke.player_y_m !== undefined && stroke.player_y_m < 0) ? 'bottom' : 'top';
          const opponentPlayer = hittingPlayer === 'bottom' ? 'top' : 'bottom';

          // Calculate ball position at halfway point of x and y1 for the hitting player
          const ballX = (stroke.player_x1_px + stroke.player_x2_px) / 2;
          const ballY = stroke.player_y1_px;

          // Map stroke_side and stroke_type to stroke type
          let strokeType = 'forehand'; // default
          if (stroke.stroke_side === 'overhead') {
            if (stroke.stroke_type === 'serve') {
              strokeType = 'serve';
            } else {
              strokeType = 'smash';
            }
          } else if (stroke.stroke_side) {
            // Map stroke_side directly to stroke_type (forehand/backhand)
            strokeType = stroke.stroke_side;
          } else if (stroke.stroke_type && stroke.stroke_type !== 'serve') {
            // Use stroke_type if stroke_side is not available
            strokeType = stroke.stroke_type;
          }

          // Create ball annotation for the stroke
          const ballAnnotation = {
            id: Date.now() + i,
            x: ballX,
            y: ballY,
            type: 'stroke',
            timestamp: timestamp,
            player: hittingPlayer,
            strokeType: strokeType,
            // Rally grouping drives the sidebar counter and rally navigation. Prefer the
            // predicted id, falling back to the annotated one for files without it.
            rallyId: stroke.pred_rally_id ?? stroke.rally_id ?? null
          };
          // Pipeline-generated strokes files use snake_case like every other
          // column; the app's own exports use camelCase.
          if (stroke.flaggedForReview || stroke.flagged_for_review) {
            ballAnnotation.flaggedForReview = true;
          }
          if (stroke.customLabel && stroke.customLabel !== 'none') {
            ballAnnotation.customLabel = stroke.customLabel;
          }
          // A strokes file states volleys outright: no bounce followed the stroke.
          if (stroke.bounce_frame !== undefined && stroke.bounce_frame !== null) {
            ballAnnotation.isVolley = stroke.bounce_frame === NO_BOUNCE_FRAME;
          }
          // Link back to the raw record so in/out edits can be written to the source file
          ballAnnotation.strokeIndex = i;
          if (typeof stroke.in === 'boolean') {
            ballAnnotation.strokeIn = stroke.in;
            ballAnnotation.lineConfidence = stroke.line_confidence ?? null;
          }
          loadedAnnotations.push(ballAnnotation);

          // Create player annotation for the hitting player
          loadedPlayerAnnotations.push({
            id: Date.now() + i + 10000,
            player: hittingPlayer,
            boundingBox: {
              x1: stroke.player_x1_px,
              y1: stroke.player_y1_px,
              x2: stroke.player_x2_px,
              y2: stroke.player_y2_px
            },
            timestamp: timestamp,
            strokeScore: stroke.stroke_score || 0,
            strokeType: strokeType,
            x_m: stroke.player_x_m,
            y_m: stroke.player_y_m
          });

          // Create player annotation for the opponent
          loadedPlayerAnnotations.push({
            id: Date.now() + i + 20000,
            player: opponentPlayer,
            boundingBox: {
              x1: stroke.opponent_x1_px,
              y1: stroke.opponent_y1_px,
              x2: stroke.opponent_x2_px,
              y2: stroke.opponent_y2_px
            },
            timestamp: timestamp,
            strokeScore: 0,
            strokeType: null,
            x_m: stroke.opponent_x_m,
            y_m: stroke.opponent_y_m
          });
        });
      } else {
        // Old format: object with ball_annotations or annotations array
        data = rawData;
        // Support both old format (annotations) and new format (ball_annotations)
        annotationsArray = data.ball_annotations || data.annotations;
        if (!Array.isArray(annotationsArray)) throw new Error('No annotations array');

        // Get current video dimensions
        const currentVideoWidth = playerRef.current?.videoWidth || 1280;
        const currentVideoHeight = playerRef.current?.videoHeight || 720;

        // Get saved video dimensions (default to current if not available)
        const savedVideoWidth = data.video_width || currentVideoWidth;
        const savedVideoHeight = data.video_height || currentVideoHeight;

        // Convert frame numbers to timestamps using frame index (exact PTS) or FPS fallback.
        // If annotation file has FPS, use it as the authoritative source and update global FPS.
        const fps = data.fps || getFPS();
        if (!fps) {
          throw new Error('No FPS value found in annotation file and no FPS set in app');
        }
        if (data.fps) {
          setFPS(data.fps);
        }

        loadedAnnotations = annotationsArray.reduce((acc, a, i) => {
          const rawTime = a.time || 0;
          if (rawTime < 0) return acc; // discard entries with negative timestamps

          const frameNumber = Math.round(rawTime * fps);
          let timestamp = getTimeForFrame(frameNumber);

          const annotation = {
            id: a.id || Date.now() + i,
            x: a.x,
            y: a.y,
            type: a.type || a.ball_type,
            timestamp: timestamp,
            player: a.player || 'bottom',
            strokeType: a.strokeType || 'forehand',
            playerId: a.playerId || null,
            pointScore: a.pointScore || null,
            gameScore: a.gameScore || null,
            setScore: a.setScore || null,
            rallyId: a.rallyId ?? null,
          };

          if (a.boundingBox) {
            annotation.boundingBox = a.boundingBox;
          }
          if (a.flaggedForReview) {
            annotation.flaggedForReview = true;
          }
          if (a.customLabel && a.customLabel !== 'none') {
            annotation.customLabel = a.customLabel;
          }

          acc.push(annotation);
          return acc;
        }, []);

        // Load player annotations if they exist
        loadedPlayerAnnotations = [];
        if (data.player_annotations && Array.isArray(data.player_annotations)) {
          loadedPlayerAnnotations = data.player_annotations.map((a, i) => {
            const frameNumber = Math.round((a.time || 0) * fps);
            let timestamp = getTimeForFrame(frameNumber);

            return {
              id: a.id || Date.now() + i + 10000, // Use different ID range
              player: a.player,
              boundingBox: a.boundingBox,
              timestamp: timestamp,
              strokeScore: a.strokeScore || 0, // Default to 0 if not present
              strokeType: a.strokeType || null // Default to null if not present
            };
          });
        }
      }

      // Load trajectories if present (only in old/ball_annotations format)
      if (!isNewStrokeFormat && data && Array.isArray(data.trajectories)) {
        setTrajectories(data.trajectories.map(t => ({
          startFrame: t.startFrame,
          endFrame: t.endFrame,
          bounceFrame: t.bounceFrame ?? t.endFrame,
          points: Array.isArray(t.points) ? t.points : [],
        })));
      } else {
        setTrajectories([]);
      }

      setAnnotations(loadedAnnotations);
      setPlayerAnnotations(loadedPlayerAnnotations);
      setStrokeRecords(loadedStrokeRecords);
      return { success: true, filename: fileName };
    } catch (err) {
      console.error('Error loading annotations:', err);
      return { success: false, error: 'Invalid annotation file.' };
    }
  };

  // Add player annotation at current time
  // Returns true if annotation was added, false if blocked (e.g., usage limit)
  const addPlayerAnnotation = async (player, boundingBox) => {
    if (!playerRef.current) return false;

    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);
    const snappedTime = getTimeForFrame(currentFrame);

    // Check if player annotation already exists at this frame
    const existingAnnotation = playerAnnotations.find(ann =>
      getFrameForTime(ann.timestamp) === currentFrame && ann.player === player
    );

    if (existingAnnotation) {
      // Update existing player annotation - preserve stroke data
      setPlayerAnnotations(prev => prev.map(ann =>
        getFrameForTime(ann.timestamp) === currentFrame && ann.player === player
          ? {
            ...ann,
            boundingBox,
            // Preserve existing stroke data
            strokeScore: ann.strokeScore || 0,
            strokeType: ann.strokeType || null
          }
          : ann
      ));
      return true;
    } else {
      // Creating NEW annotation - check usage FIRST before adding
      try {
        const result = await usageTrackingService.trackAnnotationCreated('player', player);
        
        if (!result.success && result.needsUpgrade) {
          console.warn('Usage limit reached - blocking player annotation');
          if (onUpgradeNeeded) {
            onUpgradeNeeded('limit_reached');
          }
          return false;
        }
        
        if (!result.canContinue) {
          console.warn('Cannot add player annotation:', result.error);
          return false;
        }
      } catch (err) {
        console.error('Error checking usage:', err);
        // On error, allow the annotation but log it
      }

      // Usage check passed - add the annotation
      const newPlayerAnnotation = {
        id: Date.now() + Math.random(),
        player,
        boundingBox,
        timestamp: snappedTime,
        strokeScore: 0,
        strokeType: null
      };

      setPlayerAnnotations(prev => [...prev, newPlayerAnnotation]);
      return true;
    }
  };

  // Add player annotation with meter coordinates at current time
  // Returns true if annotation was added, false if blocked (e.g., usage limit)
  const addPlayerAnnotationWithMeters = async (player, boundingBox, x_m, y_m) => {
    if (!playerRef.current) return false;

    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);
    const snappedTime = getTimeForFrame(currentFrame);

    // Check if player annotation already exists at this frame
    const existingAnnotation = playerAnnotations.find(ann =>
      getFrameForTime(ann.timestamp) === currentFrame && ann.player === player
    );

    if (existingAnnotation) {
      // Update existing player annotation - preserve stroke data
      setPlayerAnnotations(prev => prev.map(ann =>
        getFrameForTime(ann.timestamp) === currentFrame && ann.player === player
          ? {
            ...ann,
            boundingBox,
            x_m,
            y_m,
            // Preserve existing stroke data
            strokeScore: ann.strokeScore || 0,
            strokeType: ann.strokeType || null
          }
          : ann
      ));
      return true;
    } else {
      // Creating NEW annotation - check usage FIRST before adding
      try {
        const result = await usageTrackingService.trackAnnotationCreated('player', player);
        
        if (!result.success && result.needsUpgrade) {
          console.warn('Usage limit reached - blocking player annotation');
          if (onUpgradeNeeded) {
            onUpgradeNeeded('limit_reached');
          }
          return false;
        }
        
        if (!result.canContinue) {
          console.warn('Cannot add player annotation:', result.error);
          return false;
        }
      } catch (err) {
        console.error('Error checking usage:', err);
        // On error, allow the annotation but log it
      }

      // Usage check passed - add the annotation
      const newPlayerAnnotation = {
        id: Date.now() + Math.random(),
        player,
        boundingBox,
        timestamp: snappedTime,
        x_m,
        y_m,
        strokeScore: 0,
        strokeType: null
      };

      setPlayerAnnotations(prev => [...prev, newPlayerAnnotation]);
      return true;
    }
  };

  // Remove player annotation at current time
  const removePlayerAnnotation = (player) => {
    if (!playerRef.current) return;

    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);

    setPlayerAnnotations(prev => prev.filter(ann =>
      !(getFrameForTime(ann.timestamp) === currentFrame && ann.player === player)
    ));
  };

  // Remove all player annotations at current time
  const removeAllPlayerAnnotationsAtTime = () => {
    if (!playerRef.current) return;

    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);

    setPlayerAnnotations(prev => prev.filter(ann =>
      getFrameForTime(ann.timestamp) !== currentFrame
    ));
  };

  // Get current player annotations - uses frame-indexed Map for O(1) lookup
  const getCurrentPlayerAnnotations = () => {
    if (!playerRef.current) return [];

    const videoCurrentTime = playerRef.current.currentTime();
    const frameNumber = getFrameForTime(videoCurrentTime);
    
    return playerAnnotationsByFrame.get(frameNumber) || [];
  };

  // Clear all annotations
  const clearAnnotations = () => {
    setAnnotations([]);
    setPlayerAnnotations([]);
    setTrajectories([]);
    setStrokeRecords(null);
  };

  return {
    annotations,
    playerAnnotations,
    trajectories,
    currentAnnotations,
    annotationsByFrame,
    playerAnnotationsByFrame,
    annotationMode,
    currentPlayer,
    currentStrokeType,
    playerFilter,
    strokeTypeFilter,
    volleyFilter,
    volleyProgress,
    annotationCounts,
    annotationProgress,
    reviewCounts,
    customLabels,
    addAnnotation,
    removeAnnotation,
    addPlayerAnnotation,
    addPlayerAnnotationWithMeters,
    removePlayerAnnotation,
    removeAllPlayerAnnotationsAtTime,
    getCurrentPlayerAnnotations,
    setPlayer,
    setStrokeType,
    cyclePlayer,
    cycleShotType,
    switchAnnotationMode,
    rallyGroups,
    navigateToAnnotation,
    navigateToReview,
    navigateToRally,
    navigateToCustomLabel,
    toggleFlagForReview,
    currentStrokeIn,
    currentStrokeInfo,
    setStrokeInCall,
    saveStrokesFile,
    buildStrokeRecordsForSave,
    hasStrokeRecords: !!strokeRecords,
    cycleCustomLabel,
    addCustomLabel,
    removeCustomLabel,
    setCustomLabels,
    saveAnnotations,
    loadAnnotations,
    clearAnnotations,
    setPlayerFilter,
    setStrokeTypeFilter,
    setVolleyFilter,
    setAnnotations,
    setPlayerAnnotations
  };
};

// Helper function for time formatting (import from timeUtils)
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 100);
  return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
}; 